import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  useActivateExternalPartnerMutation,
  useApproveExternalPartnerMutation,
  useDeactivateExternalPartnerMutation,
  useDeleteExternalPartnerMutation,
  useGetExternalPartnersQuery,
} from '../../../../../redux/services/externalPartners.jsx';
import { QuestionModal, Spinner } from '../../../../common/index.jsx';
import { AppBarCMS } from '../../../../basic/index.jsx';
import { routes } from '../../../../../utils/helpers/ApiClient.jsx';
import { PlusOutlined } from '@ant-design/icons';
import ExternalPartnersTable from './ExternalPartnersTable.jsx';
import { useToastMessages } from '../../../../../hooks/useToastMessages';

const ExternalPartnersPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { deleteSuccess: toastDeleteSuccess, successMessage } =
    useToastMessages();

  useEffect(() => {
    document.title = t('Externí partneri FRI') + ' - FRI CMS';
  }, []); // Runs on mount

  const {
    data: partnersData,
    isLoading,
    refetch,
  } = useGetExternalPartnersQuery();
  const [deleteExternalPartner] = useDeleteExternalPartnerMutation();
  const [approveExternalPartner] = useApproveExternalPartnerMutation();
  const [activateExternalPartner] = useActivateExternalPartnerMutation();
  const [deactivateExternalPartner] = useDeactivateExternalPartnerMutation();

  const [rowToBeApproved, setRowToBeApproved] = useState(null);
  const [rowToBeActivated, setRowToBeActivated] = useState(null);
  const [rowToBeDeactivated, setRowToBeDeactivated] = useState(null);

  const handleDelete = async (id) => {
    try {
      await deleteExternalPartner(id).unwrap();
      toastDeleteSuccess();
      refetch();
    } catch (err) {}
  };

  const handleApprove = async (id) => {
    try {
      await approveExternalPartner(id).unwrap();
      successMessage({
        message: t('Nový externý partner FRI bol schválený.'),
      });
      refetch();
    } catch (err) {}
  };

  const handleActivate = async (id) => {
    try {
      await activateExternalPartner(id).unwrap();
      successMessage({
        message: t('Externému partnerovi FRI bol opätovne povolený prístup.'),
      });
      refetch();
    } catch (err) {}
  };

  const handleDeactivate = async (id) => {
    try {
      await deactivateExternalPartner(id).unwrap();
      successMessage({
        message: t('Externému partnerovi FRI bol zrušený prístup.'),
      });
      refetch();
    } catch (err) {}
  };

  return isLoading ? (
    <Spinner />
  ) : (
    <>
      <AppBarCMS
        title={t('Externí partneri FRI')}
        backUrl={routes.cmsDashboard}
        backUrlTooltip={t('Nástenka')}
        mainActions={[
          {
            title: t('Pridať externého partnera FRI'),
            icon: <PlusOutlined />,
            permissions: 'external-partners-admin',
            onClick: () => {
              navigate(routes.cmsExternalPartnersCreate);
            },
          },
        ]}
      />
      <div style={{ margin: '10px' }}>
        <ExternalPartnersTable
          partnersData={partnersData}
          refetch={refetch}
          onEditClick={(record) =>
            navigate(routes.cmsExternalPartnersEdit(record.id))
          }
          onDeleteClick={(record) => handleDelete(record.id)}
          onApproveClick={setRowToBeApproved}
          onActivateClick={setRowToBeActivated}
          onDeactivateClick={setRowToBeDeactivated}
        />
      </div>

      <QuestionModal
        chosenPositive={() => handleApprove(rowToBeApproved.id)}
        question={t('Naozaj chcete schváliť externého partnera FRI?')}
        visible={rowToBeApproved !== null}
        yes={t('Áno')}
        toggle={() => setRowToBeApproved(null)}
        headerTitle={t('Schváliť')}
      />

      <QuestionModal
        chosenPositive={() => handleActivate(rowToBeActivated.id)}
        question={t(
          'Naozaj chcete znovu povoliť prístup externému partnerovi FRI?',
        )}
        visible={rowToBeActivated !== null}
        yes={t('Áno')}
        toggle={() => setRowToBeActivated(null)}
        headerTitle={t('Povoliť prístup')}
      />

      <QuestionModal
        chosenPositive={() => handleDeactivate(rowToBeDeactivated.id)}
        question={t('Naozaj chcete zrušiť prístup externému partnerovi FRI?')}
        visible={rowToBeDeactivated !== null}
        yes={t('Áno')}
        toggle={() => setRowToBeDeactivated(null)}
        headerTitle={t('Zrušiť prístup')}
      />
    </>
  );
};

export default ExternalPartnersPage;
